import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import FileAPI from 'services/api/file-updated.api';
import { BulkUpdateResponse, FileAction } from 'interface/file/file.interface';
import { FILE_STATUS } from 'pages/ExperienceCanvas/types';
import { showSnackbar } from 'store/snackbar/snackbar.action';

type ActionLabels = {
  successSingular: string; // e.g. "paused"
  successPlural?: string; // optional override; defaults to successSingular
};

const DEFAULT_LABELS: Record<FileAction, ActionLabels> = {
  PROCESS: { successSingular: 'queued for processing' },
  PAUSE: { successSingular: 'paused' },
  RESUME: { successSingular: 'resumed' },
};

type RunByIdsArgs = {
  action: FileAction;
  fileIds: string[];
  optimisticStatus: FILE_STATUS;
};

export default function useFileActionRunner() {
  const fileAPI = useMemo(() => new FileAPI(), []);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [localFileStatuses, setLocalFileStatuses] = useState<Record<string, string>>({});
  const [localFileErrors, setLocalFileErrors] = useState<Record<string, string>>({});
  const [isRunning, setIsRunning] = useState(false);
  const [inFlightFileIds, setInFlightFileIds] = useState<Set<string>>(new Set());

  const applyOptimistic = useCallback((ids: string[], status: FILE_STATUS) => {
    setLocalFileStatuses((prev) => ({
      ...prev,
      ...Object.fromEntries(ids.map((id) => [id, status])),
    }));
    setLocalFileErrors((prev) => {
      const next = { ...prev };
      ids.forEach((id) => delete next[id]);
      return next;
    });
  }, []);

  const reconcile = useCallback((response: BulkUpdateResponse, optimisticIds: string[]) => {
    const erroredIds = new Set<string>();
    const errorMessages: Record<string, string> = {};
    response.results.forEach((r) => {
      if (r.status === 'ERROR') {
        erroredIds.add(r.fileId);
        errorMessages[r.fileId] = r.statusMessage || 'Action failed.';
      }
    });

    setLocalFileStatuses((prev) => {
      const next = { ...prev };
      optimisticIds.forEach((id) => {
        if (erroredIds.has(id)) delete next[id];
      });
      return next;
    });

    if (Object.keys(errorMessages).length > 0) {
      setLocalFileErrors((prev) => ({ ...prev, ...errorMessages }));
    }

    if (erroredIds.size > 0) {
      setInFlightFileIds((prev) => {
        const next = new Set(prev);
        erroredIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, []);

  const announce = useCallback(
    (response: BulkUpdateResponse) => {
      const { action, status, summary } = response;
      const labels = DEFAULT_LABELS[action];
      const verb = labels.successPlural ?? labels.successSingular;
      const fileWord = (n: number) => `${n} file${n !== 1 ? 's' : ''}`;

      if (status === 'ALL_SUCCESS') {
        dispatch(
          showSnackbar({
            content:
              action === 'PROCESS' ? 'Your files will be processed shortly' : `${fileWord(summary.succeeded)} ${verb}.`,
            type: 'success',
          }),
        );
      } else if (status === 'PARTIAL_SUCCESS') {
        dispatch(
          showSnackbar({
            content: `${fileWord(summary.succeeded)} ${verb}. ${summary.failed} failed.`,
            type: 'warning',
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            content: `Failed to ${action.toLowerCase()} ${fileWord(summary.failed)}. Please try again.`,
            type: 'error',
          }),
        );
      }
    },
    [dispatch],
  );

  const finalize = useCallback(
    async (optimisticIds: string[]) => {
      await queryClient.invalidateQueries(['ctx-data-file']);
      // Drop the optimistic overlay now that the server cache has been
      // refreshed; otherwise stale local statuses mask subsequent updates
      // performed elsewhere (e.g. paused in dropdown, resumed in slideout).
      setLocalFileStatuses((prev) => {
        const next = { ...prev };
        optimisticIds.forEach((id) => delete next[id]);
        return next;
      });
    },
    [queryClient],
  );

  const runByIds = useCallback(
    async ({ action, fileIds, optimisticStatus }: RunByIdsArgs) => {
      if (fileIds.length === 0) return null;
      setIsRunning(true);
      setInFlightFileIds((prev) => {
        const next = new Set(prev);
        if (action === 'PROCESS' || action === 'RESUME') {
          fileIds.forEach((id) => next.add(id));
        } else if (action === 'PAUSE') {
          fileIds.forEach((id) => next.delete(id));
        }
        return next;
      });
      applyOptimistic(fileIds, optimisticStatus);
      try {
        const response = await fileAPI.applyFileAction(action, fileIds);
        reconcile(response, fileIds);
        announce(response);
        await finalize(fileIds);
        return response;
      } catch (err) {
        setLocalFileStatuses((prev) => {
          const next = { ...prev };
          fileIds.forEach((id) => delete next[id]);
          return next;
        });
        setInFlightFileIds((prev) => {
          const next = new Set(prev);
          fileIds.forEach((id) => next.delete(id));
          return next;
        });
        dispatch(
          showSnackbar({
            content: `Failed to ${action.toLowerCase()} files. Please try again.`,
            type: 'error',
          }),
        );
        return null;
      } finally {
        setIsRunning(false);
      }
    },
    [applyOptimistic, reconcile, announce, finalize, fileAPI, dispatch],
  );

  return {
    localFileStatuses,
    localFileErrors,
    isRunning,
    inFlightFileIds,
    setInFlightFileIds,
    runByIds,
    setLocalFileStatuses,
  };
}
